import { useNavigate } from 'react-router-dom';
import type { InboxAlertItem, NotificationDto } from '@cosvision/shared';
import {
  useInboxListQuery,
  useInboxUnreadQuery,
  useMarkAlertReadOptimistic,
  useMarkAllActivityReadMutation,
  useMarkNotificationReadOptimistic,
} from '../../hooks/useInbox.js';
import { Sheet } from '../ui/Sheet.js';
import { ActivityRow, AlertRow, SectionHeader } from './InboxBell.js';

interface MobileInboxSheetProps {
  open: boolean;
  onClose: () => void;
  /** True for the CO — controls whether the financial Alerts section renders. */
  alertsEnabled: boolean;
}

/**
 * Full-screen inbox for sub-`lg` viewports (android-app-plan M2.3). Same
 * data, hooks, and row components as the desktop `InboxBell` dropdown —
 * only the container differs (Sheet instead of anchored popover).
 */
export function MobileInboxSheet({ open, onClose, alertsEnabled }: MobileInboxSheetProps) {
  const countQ = useInboxUnreadQuery(open);
  const listQ = useInboxListQuery(open);
  const markNotif = useMarkNotificationReadOptimistic();
  const markAlert = useMarkAlertReadOptimistic();
  const markAll = useMarkAllActivityReadMutation();
  const navigate = useNavigate();

  const activity = listQ.data?.activity ?? [];
  const alerts = alertsEnabled ? (listQ.data?.alerts ?? []) : [];
  const showAlertsSection = alertsEnabled && alerts.length > 0;
  const activityUnread = countQ.data?.activity ?? 0;

  function openActivity(n: NotificationDto): void {
    if (!n.isRead) markNotif.mutate(n.id);
    onClose();
    if (n.linkPath) navigate(n.linkPath);
  }

  function openAlert(a: InboxAlertItem): void {
    if (!a.isRead) markAlert.mutate(a.id);
    onClose();
    navigate('/#alerts-panel');
    requestAnimationFrame(() => {
      document.getElementById('alerts-panel')?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    });
  }

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Inbox"
      headerEnd={
        activityUnread > 0 ? (
          <button
            type="button"
            className="shrink-0 text-xs font-medium text-bn-700 hover:text-bn-600 disabled:opacity-50"
            onClick={() => markAll.mutate()}
            disabled={markAll.isPending}
          >
            Mark activity read
          </button>
        ) : undefined
      }
    >
      {listQ.isLoading ? (
        <p className="px-4 py-8 text-center text-sm text-slate-400">Loading…</p>
      ) : activity.length === 0 && alerts.length === 0 ? (
        <p className="px-4 py-8 text-center text-sm text-slate-400">You&rsquo;re all caught up.</p>
      ) : (
        <>
          <SectionHeader label="Activity" />
          {activity.length === 0 ? (
            <p className="px-4 py-3 text-sm text-slate-400">No recent activity.</p>
          ) : (
            <ul className="divide-y divide-slate-50">
              {activity.map((n) => (
                <ActivityRow key={n.id} item={n} onOpen={openActivity} />
              ))}
            </ul>
          )}
          {showAlertsSection ? (
            <>
              <SectionHeader label="Financial alerts" />
              <ul className="divide-y divide-slate-50">
                {alerts.map((a) => (
                  <AlertRow key={a.id} item={a} onOpen={openAlert} />
                ))}
              </ul>
            </>
          ) : null}
        </>
      )}
    </Sheet>
  );
}
